import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  AuthService,
  AuthenticationResponseModel,
  DsCredentialsRequestBody,
  DsCredentialsResponseBody,
  DsHashRequestBody,
  DsKeyInfo,
  DsKeyValidationRequestBody,
  DsKeyValidationResponseBody,
  DsSignRequestBody,
  DsSignResponseBody,
  LiloLoginWithHash,
  setAccessToken,
  setDsToken,
  setRefreshToken,
} from '@lib/auth';
import { Store } from '@ngrx/store';
import { NgOtpInputModule } from 'ng-otp-input';
import { EMPTY, catchError, concatMap, take, tap } from 'rxjs';

import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';

import { PHONE_REGEX } from '@lib/core/constants/general.constant';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

enum authSteps {
  Credentials = 1,
  Key = 2,
}

interface CredentialsForm {
  phoneNumber: FormControl<string | null>;
  password: FormControl<string | null>;
}

interface KeyForm {
  key: FormControl<DsKeyInfo | null>;
  password: FormControl<string | null>;
}

@Component({
  selector: 'auth-ds',
  standalone: true,
  imports: [LoaderComponent, MatFormField, MatIcon, MatInput, NgOtpInputModule, ReactiveFormsModule, MatLabel, MatSelect, MatOption, NgClass, NgTemplateOutlet],
  templateUrl: './auth-ds.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthDsComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);

  protected readonly authSteps = authSteps;

  protected loading: WritableSignal<boolean> = signal(false);
  protected currentStep: WritableSignal<number> = signal(this.authSteps.Credentials);

  protected credentialsForm!: FormGroup<CredentialsForm>;
  protected keyForm!: FormGroup<KeyForm>;

  protected dsCredentialsResponse: DsCredentialsResponseBody | null = null;

  ngOnInit(): void {
    this.initForms();
  }

  ngOnDestroy() {
    this.store.dispatch(setDsToken({ dsToken: null }));
  }

  private initForms() {
    this.credentialsForm = this.fb.group<CredentialsForm>({
      phoneNumber: this.fb.control(null, [Validators.required, Validators.pattern(PHONE_REGEX)]),
      password: this.fb.control('', [Validators.required]),
    });

    this.keyForm = this.fb.group<KeyForm>({
      key: this.fb.control(null, [Validators.required]),
      password: this.fb.control(null, [Validators.required]),
    });
  }

  protected changeStep(step: number) {
    if (step === this.authSteps.Credentials) {
      this.keyForm.reset();
    }
    this.currentStep.set(step);
  }

  protected submitCredentials() {
    if (this.credentialsForm.invalid) {
      return;
    }
    this.loading.set(true);
    let requestBody: DsCredentialsRequestBody;
    if (this.credentialsForm.get('phoneNumber')!.value && this.credentialsForm.get('password')!.value) {
      requestBody = {
        UserName: this.credentialsForm.get('phoneNumber')!.value!,
        Password: this.credentialsForm.get('password')!.value!,
      };
    }
    this.authService
      .postDsCredentials(requestBody!)
      .pipe(take(1))
      .subscribe({
        next: (keys: DsCredentialsResponseBody) => {
          this.dsCredentialsResponse = keys;
          this.store.dispatch(setDsToken({ dsToken: keys.Token }));
          this.authService.dsSignResult$.next(true);
          this.changeStep(this.authSteps.Key);
          this.loading.set(false);
        },
        error: () => {
          this.snackBar.open('Не вдалося залогінитися в DepositSign. Перевірите правильність введених даних', '', { duration: 10000 });
          this.loading.set(false);
        },
      });
  }

  protected submitKey(dsLogin: string, keyData: { key: DsKeyInfo; password: string }) {
    if (!keyData.key || !keyData.password) {
      return;
    }
    this.loading.set(true);

    const dsAccessToken = this.authService.depositSignToken();
    const hash: DsHashRequestBody = {
      login: dsLogin,
    };
    const payload: DsKeyValidationRequestBody = {
      KeyName: keyData.key.KeyName,
      Password: keyData.password,
    };

    this.authService
      .postDsKeyValidation(payload)
      .pipe(
        catchError(() => {
          this.snackBar.open('Не вдалося провалідувати ключ', '', { duration: 10000 });
          return EMPTY;
        }),
        tap(() => {
          this.store.dispatch(setDsToken({ dsToken: null }));
          this.authService.dsSignResult$.next(false);
        }),
        concatMap((validationResult: DsKeyValidationResponseBody) => {
          if (!validationResult.IsValid) {
            this.store.dispatch(setDsToken({ dsToken: dsAccessToken }));
            this.snackBar.open('Дані ключа не пройшли валідацію. Переконайтеся у відповідності ключа та паролю', '', { duration: 10000 });
            return EMPTY;
          }
          return this.authService.getHashForDs(hash);
        }),
        catchError(() => {
          this.store.dispatch(setDsToken({ dsToken: dsAccessToken }));
          this.snackBar.open('Не вдалося отримати хеш з бекенду lilo', '', { duration: 10000 });
          return EMPTY;
        }),
        tap(() => {
          this.store.dispatch(setDsToken({ dsToken: dsAccessToken }));
          this.authService.dsSignResult$.next(true);
        }),
        concatMap((hash: string) => {
          const dsSignRequestBody: DsSignRequestBody = {
            Password: payload.Password,
            FileHash: hash,
            KeyName: payload.KeyName,
          };
          return this.authService.signHash(dsSignRequestBody);
        }),
        catchError(() => {
          this.snackBar.open('Не вдалося підписати хеш', '', { duration: 10000 });
          return EMPTY;
        }),
        tap(() => {
          this.store.dispatch(setDsToken({ dsToken: null }));
          this.authService.dsSignResult$.next(false);
        }),
        concatMap((signedHash: DsSignResponseBody) => {
          const loginRequestBody: LiloLoginWithHash = {
            login: hash.login,
            signedHash: signedHash.SignedHash,
          };
          return this.authService.loginWithDs(loginRequestBody);
        }),
        catchError(() => {
          this.store.dispatch(setDsToken({ dsToken: dsAccessToken }));
          this.snackBar.open('Не вдалось авторизуватися', '', { duration: 10000 });
          return EMPTY;
        }),
        tap((response: AuthenticationResponseModel) => {
          this.store.dispatch(setAccessToken({ accessToken: response.accessToken }));
          this.store.dispatch(setRefreshToken({ refreshToken: response.refreshToken }));
          this.authService.successAuthResult$.next(true);
        }),
        catchError(() => {
          this.snackBar.open('Не вдалося отримати профіль користувача', '', { duration: 10000 });
          return EMPTY;
        }),
        take(1)
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Успішно авторизовано', '', { duration: 10000 });
        },
        error: () => {
          this.loading.set(false);
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }
}
